import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { CONGVIECKHAC, TRANG_THAI_CONG_VIEC_KHAC_OPTIONS } from '../constants';
import { Get, Post, Put, DeleteSingle } from '../../utils/httpProvider';
import { CONGVIECKHAC_API } from '../../utils/apiUrl.js';

// ---------------------------
// Async Thunks
// ---------------------------

// Lấy danh sách công việc khác theo nhiệm vụ
export const congViecKhacGetByNhiemVuAction = createAsyncThunk(
    CONGVIECKHAC.GET_BY_NHIEM_VU,
    async (nhiemVuId) => {
        return Get(CONGVIECKHAC_API.GET_BY_NHIEM_VU(nhiemVuId)).then(res => res).catch(err => err);
    }
);

// Lấy 1 công việc khác theo ID
export const congViecKhacGetItemAction = createAsyncThunk(
    CONGVIECKHAC.GET_BY_ID,
    async (id) => {
        return Get(CONGVIECKHAC_API.GET_BY_ID(id)).then(res => res).catch(err => err);
    }
);

// Thêm công việc khác mới
export const addCongViecKhacAction = createAsyncThunk(
    CONGVIECKHAC.CREATE,
    async (model, { rejectWithValue }) => {
        try {
            const res = await Post(CONGVIECKHAC_API.CREATE, null, model, false);

            if (res && typeof res.status === 'number') {
                if (res.status >= 200 && res.status < 300) {
                    let body = null;
                    try {
                        body = await res.json();
                    } catch (_) {}
                    return body || model;
                }

                let errBody = null;
                try {
                    errBody = await res.json();
                } catch (_) {}
                return rejectWithValue(errBody || 'Tạo công việc khác thất bại');
            }

            if (!res) return rejectWithValue('Tạo công việc khác thất bại');

            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// Sửa công việc khác
export const editCongViecKhacAction = createAsyncThunk(
    CONGVIECKHAC.UPDATE,
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await Put(CONGVIECKHAC_API.UPDATE(id), null, data);

            if (res && typeof res.status === 'number') {
                if (res.status >= 200 && res.status < 300) {
                    let body = null;
                    try {
                        body = await res.json();
                    } catch (_) {}
                    return body || { id, ...data };
                }

                let errBody = null;
                try {
                    errBody = await res.json();
                } catch (_) {}
                return rejectWithValue(errBody || 'Cập nhật công việc khác thất bại');
            }

            if (!res) return rejectWithValue('Cập nhật công việc khác thất bại');

            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

// Xóa công việc khác
export const deleteCongViecKhacAction = createAsyncThunk(
    CONGVIECKHAC.DELETE,
    async (id, { rejectWithValue, fulfillWithValue }) => {
        return DeleteSingle(CONGVIECKHAC_API.DELETE(id))
            .then(async res => {
                if (res.status === 204 || res.status === 200) return fulfillWithValue(res.status);

                let errBody = null;
                try { errBody = await res.json(); } catch (_) {}
                return rejectWithValue(errBody || 'Xoá công việc khác thất bại');
            })
            .catch(err => err);
    }
);

const initialState = {
    listCongViecKhac: [],
    totalCongViecKhac: 0,
    congViecKhacItem: null
};

// ---------------------------
// Slice
// ---------------------------
export const congViecKhacSlice = createSlice({
    name: CONGVIECKHAC.NAME_SLICE,
    initialState,
    reducers: {
        // Xóa item khỏi danh sách
        filterListCongViecKhac: (state, action) => {
            state.listCongViecKhac = state.listCongViecKhac.filter(x => x.id !== action.payload);
            state.totalCongViecKhac -= 1;
        },
        // Lọc theo trạng thái công việc khác
        filterByTrangThaiCongViecKhac: (state, action) => {
            const trangThai = action.payload;
            state.listCongViecKhac = state.listCongViecKhac.filter(x => x.trangThai === trangThai);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(congViecKhacGetByNhiemVuAction.fulfilled, (state, action) => {
                state.listCongViecKhac = action.payload?.data || action.payload || [];
                // If API returns totalRecords use it, otherwise try length
                state.totalCongViecKhac = action.payload?.totalRecords ?? (state.listCongViecKhac?.length ?? 0);
            })
            .addCase(addCongViecKhacAction.fulfilled, (state, action) => {
                // when created, push to list
                const item = action.payload;
                if (item) {
                    state.listCongViecKhac = [item, ...(state.listCongViecKhac || [])];
                    state.totalCongViecKhac = (state.totalCongViecKhac || 0) + 1;
                }
            })
            .addCase(editCongViecKhacAction.fulfilled, (state, action) => {
                const updated = action.payload;
                if (updated && updated.id) {
                    state.listCongViecKhac = (state.listCongViecKhac || []).map(x => x.id === updated.id ? updated : x);
                }
            })
            .addCase(congViecKhacGetItemAction.fulfilled, (state, action) => {
                state.congViecKhacItem = action.payload;
            })
            .addCase(congViecKhacGetItemAction.rejected, (state) => {
                state.congViecKhacItem = null;
            });
    }
});

// ---------------------------
// Export
// ---------------------------
export const {
    filterListCongViecKhac,
    filterByTrangThaiCongViecKhac
} = congViecKhacSlice.actions;

export default congViecKhacSlice.reducer;

// ---------------------------
// Map label trạng thái công việc khác
// ---------------------------
export const mapTrangThaiCongViecKhacLabel = (status) => {
    const byValue = TRANG_THAI_CONG_VIEC_KHAC_OPTIONS.find(x => x.value === status);
    if (byValue) return byValue.label;

    const byKey = TRANG_THAI_CONG_VIEC_KHAC_OPTIONS.find(x => x.key === status);
    if (byKey) return byKey.label;

    return status;
};
